import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { reportsAPI, projectsAPI, timeEntriesAPI, tasksAPI } from '../lib/api';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { 
  FolderKanban, CheckSquare, Clock, Play, Plus, Timer, 
  Calendar, Bell, Trophy, TrendingUp, TrendingDown, 
  Target, Zap, ArrowRight, AlertTriangle, Sparkles,
  ChevronRight, BarChart3
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Area, AreaChart } from 'recharts';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Quick Timer Component
const QuickTimer = ({ projects, onTimerStarted }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  const handleStartTimer = async () => {
    if (!selectedProject) {
      toast.error('Selectează un proiect');
      return;
    }
    setLoading(true);
    try {
      await timeEntriesAPI.create({
        project_id: selectedProject,
        description: description || 'Quick timer',
        start_time: new Date().toISOString()
      });
      toast.success('Timer pornit!');
      setIsOpen(false);
      setSelectedProject('');
      setDescription('');
      onTimerStarted();
    } catch (err) {
      toast.error('Eroare la pornirea timer-ului');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white shadow-lg">
          <Play className="w-4 h-4 mr-2" />
          Start Timer Rapid
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Pornește Timer Rapid</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Proiect</label>
            <Select value={selectedProject} onValueChange={setSelectedProject}>
              <SelectTrigger>
                <SelectValue placeholder="Selectează proiect" />
              </SelectTrigger>
              <SelectContent>
                {projects.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Descriere (opțional)</label>
            <Input 
              value={description} 
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Ce lucrezi?"
            />
          </div>
          <Button onClick={handleStartTimer} disabled={loading} className="w-full">
            {loading ? 'Se pornește...' : 'Pornește Timer'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Performance Mini Widget
const PerformanceMiniWidget = ({ performance }) => {
  if (!performance) return null;
  
  const scoreColor = performance.overall_color === 'green' ? 'text-emerald-500' : 
                     performance.overall_color === 'yellow' ? 'text-amber-500' : 'text-red-500';
  const bgColor = performance.overall_color === 'green' ? 'bg-emerald-50' : 
                  performance.overall_color === 'yellow' ? 'bg-amber-50' : 'bg-red-50';

  return (
    <Card className={`${bgColor} border-none`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-full bg-white flex items-center justify-center ${scoreColor} font-bold text-xl shadow-sm`}>
              {Math.round(performance.overall_score)}
            </div>
            <div>
              <p className="font-semibold text-sm">Scorul Tău</p>
              <p className="text-xs text-muted-foreground">
                {performance.overall_color === 'green' ? 'Excelent!' : 
                 performance.overall_color === 'yellow' ? 'Bun, continuă!' : 'Îmbunătățește-te!'}
              </p>
            </div>
          </div>
          <div className="text-right">
            {performance.rank_in_team && (
              <p className="text-lg font-bold">#{performance.rank_in_team}</p>
            )}
            <p className="text-xs text-muted-foreground">din echipă</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Upcoming Deadlines Component
const UpcomingDeadlines = ({ tasks }) => {
  const navigate = useNavigate();
  const upcomingTasks = tasks
    .filter(t => t.due_date && t.status !== 'done')
    .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))
    .slice(0, 5);

  const getDaysUntil = (date) => {
    const now = new Date();
    const due = new Date(date);
    const diff = Math.ceil((due - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const getUrgencyColor = (days) => {
    if (days < 0) return 'bg-red-100 text-red-700 border-red-200';
    if (days <= 2) return 'bg-orange-100 text-orange-700 border-orange-200';
    if (days <= 7) return 'bg-amber-100 text-amber-700 border-amber-200';
    return 'bg-slate-100 text-slate-700 border-slate-200';
  };

  if (upcomingTasks.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-500" />
            Deadline-uri Apropiate
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center py-6">
          <Calendar className="w-10 h-10 mx-auto mb-2 text-muted-foreground opacity-30" />
          <p className="text-sm text-muted-foreground">Niciun deadline apropiat</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Calendar className="w-5 h-5 text-blue-500" />
          Deadline-uri Apropiate
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {upcomingTasks.map(task => {
          const days = getDaysUntil(task.due_date);
          return (
            <div 
              key={task.id} 
              className={`p-3 rounded-lg border ${getUrgencyColor(days)} cursor-pointer hover:shadow-sm transition-shadow`}
              onClick={() => navigate(`/projects/${task.project_id}`)}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="font-medium text-sm">{task.title}</p>
                  <p className="text-xs opacity-75">{task.project_name}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-sm">
                    {days < 0 ? `${Math.abs(days)}z întârziere` : 
                     days === 0 ? 'Azi!' : 
                     days === 1 ? 'Mâine' : `${days} zile`}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

// Recent Activity / Notifications
const RecentActivity = ({ alerts, badges }) => {
  const navigate = useNavigate();
  
  const activities = [
    ...(alerts || []).slice(0, 3).map(a => ({
      type: 'alert',
      icon: AlertTriangle,
      color: a.severity === 'critical' ? 'text-red-500' : 'text-amber-500',
      title: a.message,
      subtitle: a.employee_name,
      time: a.created_at
    })),
    ...(badges || []).slice(0, 2).map(b => ({
      type: 'badge',
      icon: Trophy,
      color: 'text-amber-500',
      title: `Badge câștigat: ${b.name_ro || b.name}`,
      subtitle: b.description,
      time: b.earned_at
    }))
  ].sort((a, b) => new Date(b.time) - new Date(a.time)).slice(0, 5);

  if (activities.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Bell className="w-5 h-5 text-purple-500" />
            Activitate Recentă
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center py-6">
          <Sparkles className="w-10 h-10 mx-auto mb-2 text-muted-foreground opacity-30" />
          <p className="text-sm text-muted-foreground">Nicio activitate recentă</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Bell className="w-5 h-5 text-purple-500" />
          Activitate Recentă
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {activities.map((activity, idx) => {
          const Icon = activity.icon;
          return (
            <div key={idx} className="flex items-start gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors">
              <div className={`mt-0.5 ${activity.color}`}>
                <Icon className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{activity.title}</p>
                <p className="text-xs text-muted-foreground">{activity.subtitle}</p>
              </div>
            </div>
          );
        })}
        <Button 
          variant="ghost" 
          className="w-full mt-2 text-sm"
          onClick={() => navigate('/employee-performance')}
        >
          Vezi toate <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </CardContent>
    </Card>
  );
};

// Weekly Hours Chart
const WeeklyHoursChart = ({ timeEntries }) => {
  // Calculate hours per day for current work week (Mon-Fri only)
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
  
  // Find Monday of current week
  const monday = new Date(today);
  const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  monday.setDate(today.getDate() - daysFromMonday);
  
  const workDays = [];
  for (let i = 0; i < 5; i++) {
    const date = new Date(monday);
    date.setDate(monday.getDate() + i);
    const dateStr = date.toISOString().split('T')[0];
    const dayName = date.toLocaleDateString('ro-RO', { weekday: 'short' });
    
    const hours = timeEntries
      .filter(e => e.start_time?.startsWith(dateStr) && e.duration_minutes)
      .reduce((sum, e) => sum + (e.duration_minutes / 60), 0);
    
    workDays.push({
      day: dayName,
      hours: Math.round(hours * 10) / 10,
      date: dateStr
    });
  }

  const totalHours = workDays.reduce((s, d) => s + d.hours, 0);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-indigo-500" />
          Ore Săptămâna Aceasta
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={150}>
          <AreaChart data={workDays}>
            <defs>
              <linearGradient id="colorHours" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#f97316" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#f97316" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <XAxis dataKey="day" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis hide />
            <Tooltip 
              formatter={(value) => [`${value}h`, 'Ore']}
              contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
            />
            <Area 
              type="monotone" 
              dataKey="hours" 
              stroke="#f97316" 
              strokeWidth={2}
              fill="url(#colorHours)" 
            />
          </AreaChart>
        </ResponsiveContainer>
        <div className="flex justify-between text-xs text-muted-foreground mt-2">
          <span>Total: {totalHours.toFixed(1)}h</span>
          <span>Media: {(totalHours / 5).toFixed(1)}h/zi</span>
        </div>
      </CardContent>
    </Card>
  );
};

// Main Dashboard Component
export const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState(null);
  const [timeByProject, setTimeByProject] = useState([]);
  const [recentProjects, setRecentProjects] = useState([]);
  const [activeTimers, setActiveTimers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [performance, setPerformance] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Basic data
      const [statsRes, timeRes, projectsRes, timersRes] = await Promise.all([
        reportsAPI.getDashboard(),
        reportsAPI.getTimeByProject(),
        projectsAPI.getAll(),
        timeEntriesAPI.getAll({})
      ]);
      
      setStats(statsRes.data);
      setTimeByProject(timeRes.data.slice(0, 5));
      setRecentProjects(projectsRes.data.slice(0, 5));
      setProjects(projectsRes.data);
      setTimeEntries(timersRes.data);
      setActiveTimers(timersRes.data.filter(t => !t.end_time && t.user_id === user?.id));

      // Load all tasks for deadlines
      try {
        const tasksRes = await tasksAPI.getAll();
        setTasks(tasksRes.data || []);
      } catch (e) {
        // Tasks might need project_id
        const allTasks = [];
        for (const project of projectsRes.data.slice(0, 10)) {
          try {
            const taskRes = await tasksAPI.getAll(project.id);
            allTasks.push(...(taskRes.data || []).map(t => ({ ...t, project_name: project.name })));
          } catch {}
        }
        setTasks(allTasks);
      }

      // Performance data
      const currentMonth = new Date().toISOString().slice(0, 7);
      try {
        const perfRes = await api.get(`/reports/my-performance?month=${currentMonth}`);
        setPerformance(perfRes.data);
      } catch (e) {
        console.log('Performance not available');
      }

      // Alerts (admin/PM only)
      if (user?.role !== 'team_member') {
        try {
          const alertsRes = await api.get('/performance-alerts');
          setAlerts(alertsRes.data || []);
        } catch {}
      }

      // Badges
      try {
        const badgesRes = await api.get('/badges/my-badges');
        setBadges(badgesRes.data?.unique_badges || []);
      } catch {}

    } catch (error) {
      toast.error('Eroare la încărcarea datelor');
    } finally {
      setLoading(false);
    }
  };

  const totalWeeklyHours = timeEntries
    .filter(e => {
      if (!e.duration_minutes) return false;
      const date = new Date(e.start_time);
      const dayOfWeek = date.getDay();
      // Exclude Sâm (6) și Dum (0)
      if (dayOfWeek === 0 || dayOfWeek === 6) return false;
      // Current work week (Mon-Fri)
      const today = new Date();
      const currentDay = today.getDay();
      const daysFromMonday = currentDay === 0 ? 6 : currentDay - 1;
      const monday = new Date(today);
      monday.setDate(today.getDate() - daysFromMonday);
      monday.setHours(0, 0, 0, 0);
      return date >= monday;
    })
    .reduce((sum, e) => sum + (e.duration_minutes / 60), 0);

  const statCards = [
    { title: 'Proiecte', value: stats?.projects_count || 0, icon: FolderKanban, color: 'bg-blue-500', href: '/projects' },
    { title: 'Taskuri', value: stats?.tasks_count || 0, icon: CheckSquare, color: 'bg-emerald-500', href: '/projects' },
    { title: 'Ore Totale', value: `${stats?.total_hours || 0}h`, icon: Clock, color: 'bg-purple-500', href: '/time-tracking' },
    { title: 'Săptămâna Aceasta', value: `${totalWeeklyHours.toFixed(1)}h`, icon: Zap, color: 'bg-orange-500', href: '/time-tracking' },
  ];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header with greeting and quick actions */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-outfit text-3xl font-bold tracking-tight flex items-center gap-2" data-testid="dashboard-title">
              Bună, {user?.full_name?.split(' ')[0] || 'utilizator'}! 
              <span className="text-2xl">👋</span>
            </h1>
            <p className="mt-1 text-muted-foreground">
              {new Date().toLocaleDateString('ro-RO', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <QuickTimer projects={projects} onTimerStarted={loadData} />
            <Button variant="outline" onClick={() => navigate('/projects')}>
              <Plus className="w-4 h-4 mr-2" />
              Proiect Nou
            </Button>
          </div>
        </div>

        {/* Performance mini widget for team members */}
        {performance && <PerformanceMiniWidget performance={performance} />}

        {/* Active Timers Alert */}
        {activeTimers.length > 0 && (
          <Card className="bg-gradient-to-r from-orange-50 to-red-50 border-orange-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center">
                    <Timer className="w-5 h-5 text-white animate-pulse" />
                  </div>
                  <div>
                    <p className="font-semibold text-orange-700">Timer Activ</p>
                    <p className="text-sm text-orange-600">
                      {activeTimers[0].description || 'Timer în desfășurare'} - 
                      Pornit la {new Date(activeTimers[0].start_time).toLocaleTimeString('ro-RO')}
                    </p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  className="border-orange-300 text-orange-700 hover:bg-orange-100"
                  onClick={() => navigate('/time-tracking')}
                >
                  Gestionează <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {statCards.map((stat, idx) => {
            const Icon = stat.icon;
            return (
              <Card 
                key={idx} 
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => navigate(stat.href)}
                data-testid={`stat-card-${idx}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {stat.title}
                      </p>
                      <p className="text-2xl font-bold mt-1">{stat.value}</p>
                    </div>
                    <div className={`${stat.color} p-2.5 rounded-lg`}>
                      <Icon className="h-5 w-5 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Charts */}
          <div className="lg:col-span-2 space-y-6">
            {/* Weekly Hours Chart */}
            <WeeklyHoursChart timeEntries={timeEntries} />

            {/* Time by Project */}
            <Card data-testid="time-chart-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <FolderKanban className="w-5 h-5 text-blue-500" />
                    Timp pe Proiecte
                  </span>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/reports')}>
                    Vezi Rapoarte <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {timeByProject.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={timeByProject} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="project_name" tick={{ fontSize: 11 }} width={100} />
                      <Tooltip 
                        formatter={(value) => [`${value}h`, 'Ore']}
                        contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
                      />
                      <Bar dataKey="total_hours" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-8">
                    <Clock className="w-10 h-10 mx-auto mb-2 text-muted-foreground opacity-30" />
                    <p className="text-muted-foreground">Nicio înregistrare de timp</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Projects */}
            <Card data-testid="recent-projects-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <FolderKanban className="w-5 h-5 text-emerald-500" />
                    Proiecte Recente
                  </span>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/projects')}>
                    Vezi Toate <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {recentProjects.length > 0 ? (
                  <div className="space-y-2">
                    {recentProjects.map((project) => (
                      <div
                        key={project.id}
                        className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors cursor-pointer"
                        onClick={() => navigate(`/projects/${project.id}`)}
                        data-testid="project-item"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{project.name}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {project.description || 'Fără descriere'}
                          </p>
                        </div>
                        <Badge variant="secondary" className="ml-2">
                          {project.status || 'activ'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FolderKanban className="w-10 h-10 mx-auto mb-2 text-muted-foreground opacity-30" />
                    <p className="text-muted-foreground">Niciun proiect încă</p>
                    <Button variant="outline" className="mt-3" onClick={() => navigate('/projects')}>
                      <Plus className="w-4 h-4 mr-2" />
                      Creează primul proiect
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Widgets */}
          <div className="space-y-6">
            {/* Deadlines */}
            <UpcomingDeadlines tasks={tasks} />

            {/* Recent Activity */}
            <RecentActivity alerts={alerts} badges={badges} />

            {/* Badges Summary */}
            {badges.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-amber-500" />
                    Badge-urile Tale
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {badges.slice(0, 4).map((badge, idx) => (
                      <div 
                        key={idx}
                        className="px-3 py-1.5 bg-amber-50 rounded-full text-sm font-medium text-amber-700 border border-amber-200"
                      >
                        🏆 {badge.name_ro || badge.name}
                      </div>
                    ))}
                  </div>
                  {badges.length > 4 && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="w-full mt-3"
                      onClick={() => navigate('/employee-performance')}
                    >
                      +{badges.length - 4} alte badge-uri
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};
